import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { PolicyTemplate, PolicyTemplateDetails } from '../../../../types/policies';
import { PolicyTemplateCategory } from '../types/category';
import { useGetPolicyTemplateRisks, useGetPolicyTemplateCategories } from '../../api';
import { PolicyTemplateRisk } from '../types/risk';

interface PolicyTemplatesContextType {
    templates: PolicyTemplate[];
    categories: PolicyTemplateCategory[];
    risks: PolicyTemplateRisk[];
    getTemplate: (templateId: PolicyTemplate['id']) => PolicyTemplate | undefined;
    onEditTemplate: (templateId: PolicyTemplate['id'], details: Partial<PolicyTemplateDetails>) => void;
    onAddTemplate: (template: PolicyTemplate) => void;
    onRemoveTemplate: (templateId: PolicyTemplate['id']) => void;
}

export const PolicyTemplatesContext = createContext<PolicyTemplatesContextType | undefined>(undefined);

export const usePolicyTemplatesContext = () => {
    const context = useContext(PolicyTemplatesContext);

    if (!context) {
        throw new Error(
            'PolicyTemplatesContext not initialized, please make sure you use it under `PolicyTemplatesContext.Provider`.'
        );
    }

    return context;
};

export const usePolicyTemplatesContextValue = (policyTemplates: PolicyTemplate[]): PolicyTemplatesContextType => {
    const [templates, setTemplates] = useState<PolicyTemplate[]>(policyTemplates);
    const { data: categories = [] } = useGetPolicyTemplateCategories();
    const { data: risks = [] } = useGetPolicyTemplateRisks();

    useEffect(() => {
        setTemplates(policyTemplates);
    }, [policyTemplates]);

    const getTemplate = useCallback(
        (templateId: PolicyTemplate['id']) => {
            return templates.find((template) => template.id === templateId);
        },
        [templates]
    );

    const onEditTemplate = useCallback((templateId: PolicyTemplate['id'], details: Partial<PolicyTemplateDetails>) => {
        setTemplates((prevTemplates) =>
            prevTemplates.map((template) => {
                if (template.id !== templateId) {
                    return template;
                }

                return {
                    ...template,
                    ...details
                };
            })
        );
    }, []);

    const onAddTemplate = useCallback((template: PolicyTemplate) => {
        setTemplates((prevTemplates) => [...prevTemplates, template]);
    }, []);

    const onRemoveTemplate = useCallback((templateId: PolicyTemplate['id']) => {
        setTemplates((prevTemplates) => prevTemplates.filter((template) => template.id !== templateId));
    }, []);

    return {
        templates,
        categories,
        risks,
        getTemplate,
        onEditTemplate,
        onAddTemplate,
        onRemoveTemplate
    };
};
